import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNotificationStack } from '../../../../app/hooks/useNotificationStack'
import { useInviteUserMutation } from '../../../../app/redux-fetch/apiQuery'
import SubmitButtons from '../../../../components/Buttons/SubmitButtons'
import DefaultSpacingGrid from '../../../../components/Containers/DefaultSpacingGrid'
import FormInput from '../../../../components/Forms/FormInput'
import FormSelect from '../../../../components/Forms/FormSelect'
import { SectionTitleTypography } from '../../../../components/Typographies/styles'
import { useSideModalContext } from '../../../Assets/modals/SideModalContext'
import { InviteUserApiError } from '../types'
import { InviteUserFormData, inviteUserSchema } from './inviteUserSchema'
import messages from './messages'

// E10 F10-2 — invite a user into the caller's workspace. The Tenant is taken from the caller's persona
// server-side, so it is not on the form. RHF + zod with mode 'onChange' so the server-set inline error on
// the email clears as the user edits it. A 409 DUPLICATE_EMAIL_IN_SCOPE renders inline on the email field
// (modal stays open, values preserved); every other error is surfaced by the shared base-query toast.
// Group/role assignment is done afterwards via the user edit flow, so it is not on this lean invite form.
const InviteUserModal = () => {
  const { formatMessage } = useIntl()
  const { onClose } = useSideModalContext()
  const { enqueueSuccess } = useNotificationStack()

  const methods = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      expiryDays: 7,
    },
  })

  const [inviteUser, { isLoading }] = useInviteUserMutation()
  const busy = isLoading || methods.formState.isSubmitting

  const expiryOptions = [
    { label: formatMessage(messages.inviteExpiry1Day), value: 1 },
    { label: formatMessage(messages.inviteExpiry3Days), value: 3 },
    { label: formatMessage(messages.inviteExpiry7Days), value: 7 },
  ]

  const onSubmit = async (values: InviteUserFormData) => {
    try {
      await inviteUser({
        body: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          expiryDays: values.expiryDays,
          userGroupIds: [],
        },
      }).unwrap()
      enqueueSuccess(messages.inviteUserSuccess)
      onClose()
    } catch (error) {
      const apiError = error as InviteUserApiError
      if (apiError?.kind === 'duplicate-email') {
        // Inline on the email field — the shared base query suppresses the toast for this code.
        methods.setError('email', {
          type: 'server',
          message: formatMessage(messages.duplicateEmailInScope),
        })
        return
      }
      // unknown: the shared base-query toast already surfaced it; keep the modal open for a retry.
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} aria-label='invite-user-form'>
        <DefaultSpacingGrid container sx={{ p: '48px', mt: '48px', mb: '48px' }}>
          <Grid display='flex' flexDirection='column' width='100%'>
            <Box sx={{ mb: '16px' }}>
              <SectionTitleTypography>{formatMessage(messages.inviteUserSectionTitle)}</SectionTitleTypography>
            </Box>
            <FormInput
              name='firstName'
              label={formatMessage(messages.firstNameLabel)}
              required
              maxLength={150}
            />
            <FormInput
              name='lastName'
              label={formatMessage(messages.lastNameLabel)}
              required
              maxLength={150}
            />
            <FormInput
              name='email'
              label={formatMessage(messages.emailLabel)}
              type='email'
              required
              maxLength={250}
            />
            <FormSelect
              name='expiryDays'
              label={formatMessage(messages.inviteExpiryLabel)}
              options={expiryOptions}
              required
            />
          </Grid>
        </DefaultSpacingGrid>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <SubmitButtons
            create
            isEnabled={true}
            submitDisabled={!methods.formState.isValid || busy}
            cancelDisabled={busy}
            onCancel={onClose}
            submitForm={methods.handleSubmit(onSubmit)}
          />
        </Box>
      </form>
    </FormProvider>
  )
}

export default InviteUserModal
